<script setup lang="ts">

import TheBottomSheet from '@/components/TheBottomSheet.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import EggImage from '@/partners/lingo/lingo-giveaway/assets/images/egg.png'

import { T } from '@tolgee/vue'
import { useTonConnectModal } from '@townsquarelabs/ui-vue'
import { useWalletConnect } from '@/composables/game/wallet-connect'
import { twMerge } from 'tailwind-merge'
import BaseLinkButton from '@/components/base/BaseLinkButton.vue'
import { isiOS } from '@/helpers/browser'
import ConnectedWallet from '@/components/ConnectedWallet.vue'
import { computed, watch } from 'vue'

const props = defineProps<{
  show: boolean
  isLoading?: boolean
  isFailed?: boolean
}>()
const emit = defineEmits(['hide', 'complete', 'onConnected'])

const { open } = useTonConnectModal()
const { rawAddress } = useWalletConnect()
const isIOSBrowser = isiOS()

const iosLink = 'https://apps.apple.com/ro/app/tonkeeper-ton-wallet/id1587742107' // iOS App Store link
const androidLink = 'https://play.google.com/store/apps/details?id=com.ton_keeper' // Google Play Store link

const failedState = computed(() => props.isFailed && rawAddress.value)

function onConnect() {
  open()
}

function onTransaction() {
  emit('complete')
}

function openTonkeeperInStore() {
  const appLink = isIOSBrowser ? iosLink : androidLink
  window.open(appLink, '_blank')
}

watch(rawAddress, () => {
  if (rawAddress.value) {
    emit('onConnected')
  }
})

</script>

<template>
  <TheBottomSheet
    :show="show"
    @hide="$emit('hide')"
    :close-button="!isLoading"
  >
    <div :class="twMerge('w-full flex flex-col items-center gap-y-8')">
      <div class="flex flex-col gap-y-6">
        <img
          :src="EggImage"
          :class="twMerge('h-[120px] w-auto object-contain', failedState && 'opacity-40')"
        >

        <div class="text-center text-base font-medium text-white/60">
          <h2
            :class="twMerge('text-solid-white font-bold text-2xl -tracking-[0.48px]', failedState && 'text-red-accent')"
          >
            <T
              v-if="isLoading"
              key-name="lingo.giveaway.transaction.loading_title"
              default-value="Transaction in progress..."
            />
            <T
              v-else-if="failedState"
              key-name="lingo.giveaway.transaction.failed_title"
              default-value="Transaction has failed"
            />
            <T
              v-else
              key-name="lingo.giveaway.transaction.title"
              default-value="Transact to reveal"
            />
          </h2>
          <p
            v-if="!failedState"
            class="text-base text-white/60 font-medium"
          >
            <T
              key-name="lingo.giveaway.transaction.description"
              default-value="Complete one small transaction to hatch your egg and collect a Maverick."
            />
          </p>
          <div
            v-else
            class="text-base text-white/60 font-medium max-w-[315px] whitespace-pre-line"
          >
            <span>If you’re using Telegram Wallet, we recommend switching to </span>
            <BaseLinkButton
              text-class="text-sm"
              @click="openTonkeeperInStore"
            >
              Tonkeeper
            </BaseLinkButton>
            <span>{{ `\nfor a smoother experience.` }}</span>
          </div>
        </div>
      </div>
      <div class="flex flex-col w-full items-center gap-y-3">
        <p
          v-if="isLoading"
          class="text-center text-white/60 text-sm font-medium"
        >
          Keep this window open until transaction is done.
        </p>
        <div
          v-else-if="!rawAddress"
          class="inline-block text-center text-white/60 text-sm font-medium"
        >
          <span>We recommend using </span>
          <BaseLinkButton
            text-class="text-sm"
            @click="openTonkeeperInStore"
          >
            Tonkeeper
          </BaseLinkButton>
          <span>for transactions.</span>
        </div>
        <BaseButton
          v-if="!rawAddress"
          class="w-full"
          @click="onConnect"
        >
          <template #leftIcon>
            <img
              src="@/assets/images/gifs/money.gif"
              class="h-full w-auto max-w-[24px]"
            >
          </template>
          <T
            key-name="wallet.connect_wallet"
            default-value="Connect wallet"
          />
        </BaseButton>
        <BaseButton
          v-else-if="!failedState"
          class="w-full"
          :loading="isLoading"
          @click="onTransaction"
        >
          <T
            v-if="isLoading"
            key-name="lingo.giveaway.transaction.loading_cta"
            default-value="Processing..."
          />
          <T
            v-else
            key-name="lingo.giveaway.transaction.cta"
            default-value="Complete Transaction"
          />
        </BaseButton>
        <ConnectedWallet v-else />
      </div>
    </div>
  </TheBottomSheet>
</template>
